import React, { useState, useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { learners as api } from "../../../../../api/learners";
import Pagination from "../../../../../components/pagination/Pagination";
import PermissionGate from "../../../../../components/PermissionGate";
import Table from "../../../../../components/table/Table";
import EmptyData from "../../../../../components/EmptyData";

// Helper: generate initials avatar background colour from name
const avatarColours = [
  "bg-blue-500", "bg-green-500", "bg-purple-500", "bg-orange-500",
  "bg-pink-500", "bg-teal-500", "bg-indigo-500", "bg-red-500",
];
const getAvatarColour = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColours[Math.abs(hash) % avatarColours.length];
};
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

const StatusBadge = ({ status }) => {
  const map = {
    completed: "text-green-600 bg-green-100",
    "in progress": "text-orange-600 bg-orange-100",
    "not started": "text-gray-500 bg-gray-100",
    active: "text-blue-600 bg-blue-100",
  };
  const cls = map[status?.toLowerCase()] ?? "text-gray-500 bg-gray-100";
  return (
    <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${cls}`}>
      {status ?? "—"}
    </span>
  );
};

const LearnersTable = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [department, setDepartment] = useState("");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const { data: response, isLoading } = useQuery(
    ["admin-learners", currentPage, perPage, search, department, status],
    () =>
      api.getLearners_Profile({
        page: currentPage,
        perPage,
        search: search || undefined,
        department: department || undefined,
        statusFilter: status || undefined,
      }),
    {
      staleTime: 30000,
      keepPreviousData: true,
    }
  );

  const learnersList = response?.data ?? [];
  const meta = response?.meta ?? {};

  const handleSearch = useCallback((e) => {
    e?.preventDefault?.();
    setSearch(searchInput.trim());
    setCurrentPage(1);
  }, [searchInput]);

  const handleDeptChange = (e) => {
    setDepartment(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setCurrentPage(1);
  };

  const viewProfile = (learner) => {
    sessionStorage.setItem("currentLearner", JSON.stringify(learner));
    navigate(`/admin/learners/profile/${learner?.id}`);
  };

  const columns = useMemo(
    () => [
      {
        title: "ID",
        selector: null,
        key: "id",
        render: ({ index }) => index + 1 + ((meta?.page || 1) - 1) * (meta?.perPage || perPage),
      },
      {
        title: "Learner",
        selector: "name",
        key: "name",
        render: ({ row }) => {
          const name = row?.name ?? row?.user?.name ?? "-";
          const initials = getInitials(name);
          const colour = getAvatarColour(name);
          return (
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-bold flex-shrink-0 ${colour}`}>
                {initials || "-"}
              </span>
              <span className="font-medium text-gray-800 text-sm">{name}</span>
            </div>
          );
        },
      },
      {
        title: "Email",
        selector: "email",
        key: "email",
        render: ({ row }) => <span className="text-primary-blue text-sm">{row?.email ?? row?.user?.email ?? "-"}</span>,
      },
      {
        title: "Department",
        selector: "department",
        key: "department",
        render: ({ item }) => <span className="text-sm">{item || "-"}</span>,
      },
      {
        title: "Job Title",
        selector: "jobTitle",
        key: "jobTitle",
        render: ({ item }) => <span className="text-sm">{item || "-"}</span>,
      },
      {
        title: "Location",
        selector: "location",
        key: "location",
        render: ({ item }) => <span className="text-sm">{item || "-"}</span>,
      },
      {
        title: "Progress",
        selector: "progress",
        key: "progress",
        render: ({ item }) => <span className="text-sm">{item ?? "-"}</span>,
      },
      {
        title: "Status",
        selector: "status",
        key: "status",
        render: ({ item }) => <StatusBadge status={item} />,
      },
      {
        title: "Action",
        selector: "id",
        key: "action",
        render: ({ row }) => (
          <div className="flex items-center gap-3">
            <PermissionGate slug="learner-management.view">
              <button
                type="button"
                onClick={() => viewProfile(row)}
                title="View Profile"
              >
                <i className="bi bi-eye text-primary-blue text-lg" />
              </button>
            </PermissionGate>
          </div>
        ),
      },
    ],
    [meta?.page, meta?.perPage, perPage]
  );

  return (
    <div className="p-5">
      <header className="flex justify-between items-center mt-1 mb-5">
        <h5 className="text-md font-semibold">Total Learners ({meta?.total ?? 0})</h5>
        <form onSubmit={handleSearch} className="flex items-center gap-3">
          <div className="relative">
            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value);
                if (!value.trim()) {
                  setSearch("");
                  setCurrentPage(1);
                }
              }}
              placeholder="Search by name or email..."
              className="input-style bg-primaryGray w-[300px] pl-8"
            />
          </div>
          <button type="submit" className="btn pt-3 pb-3 pl-6 pr-6 bg-primary-blue">
            Search
          </button>
        </form>
      </header>

      <div className="flex gap-3 mb-5">
        <select
          value={department}
          onChange={handleDeptChange}
          className="input-style bg-primaryGray min-w-[180px]"
        >
          <option value="">All Departments</option>
          <option value="Technology">Technology</option>
          <option value="Operations">Operations</option>
          <option value="Finance">Finance</option>
          <option value="HR">HR</option>
          <option value="Marketing">Marketing</option>
          <option value="Risk">Risk</option>
          <option value="Compliance">Compliance</option>
        </select>
        <select
          value={status}
          onChange={handleStatusChange}
          className="input-style bg-primaryGray min-w-[180px]"
        >
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="in progress">In Progress</option>
          <option value="not started">Not Started</option>
        </select>
      </div>

      <div className="p-5 bg-white rounded-xl">
        {isLoading ? (
          <Table columns={columns} data={[]} isLoading={isLoading} />
        ) : learnersList.length > 0 ? (
          <Table columns={columns} data={learnersList} isLoading={isLoading} />
        ) : (
          <EmptyData text="No learners found" />
        )}

        {meta?.total > 0 && (
          <Pagination
            data={meta}
            perPage={perPage}
            pageNo={currentPage}
            setPageNo={setCurrentPage}
            setPerPage={setPerPage}
          />
        )}
      </div>
    </div>
  );
};

export default LearnersTable;
